import React, { useEffect, useState } from 'react';
import { apiFetch, AUTH_USER_KEY } from '../utils/api';
import { API_BASE_URL } from '../config';
import { beerColor } from '../utils/beerColor';

interface Props {
  productId: number | string;
  productName?: string | null;
  productStyle?: string | null;
}

function isAdminUser(): boolean {
  try {
    const u = JSON.parse(localStorage.getItem(AUTH_USER_KEY) || 'null');
    return u?.role === 'Admin' || u?.role === 'SuperAdmin';
  } catch {
    return false;
  }
}

/**
 * Facility View color for a product. Shows the effective color (saved, from
 * style, or automatic). Admins can set or clear an exact color.
 */
const ProductColorField: React.FC<Props> = ({ productId, productName, productStyle }) => {
  const [saved, setSaved] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const admin = isAdminUser();

  useEffect(() => {
    let cancelled = false;
    apiFetch(`${API_BASE_URL}/api/products/${productId}`, { headers: { Accept: 'application/json' } })
      .then(async (r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (cancelled || !p) return;
        const c = typeof p.color === 'string' && p.color ? p.color : null;
        setSaved(c);
        setDraft(c || '');
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const effective = beerColor({ productId, productName, productStyle, productColor: saved });
  const sourceLabel =
    effective.source === 'product'
      ? 'Set on this product'
      : effective.source === 'style'
        ? `Estimated from style${productStyle ? ` (${productStyle})` : ''}`
        : 'Automatic (no color set)';

  const save = async (value: string | null) => {
    setMsg(null);
    const res = await apiFetch(`${API_BASE_URL}/api/products/${productId}/color`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ color: value }),
      allowHttpError: true,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      setMsg(body.error || `Save failed (${res.status})`);
      return;
    }
    setSaved(body.color || null);
    setDraft(body.color || '');
    setMsg(body.color ? 'Color saved' : 'Color cleared');
  };

  return (
    <div className="mb-3" data-testid="product-color-field">
      <label className="form-label" style={{ fontWeight: 600 }}>
        Facility View color
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span
          data-testid="product-color-swatch"
          data-color={effective.fill}
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: effective.fill,
            border: '1px solid rgba(0,0,0,0.3)',
            display: 'inline-block',
          }}
        />
        <span style={{ color: '#555', fontSize: 13 }} data-testid="product-color-source">
          {sourceLabel}
        </span>
        {admin && (
          <>
            <input
              type="color"
              aria-label="Pick color"
              data-testid="product-color-input"
              value={draft || effective.fill}
              onChange={(e) => setDraft(e.target.value.toUpperCase())}
              style={{ width: 44, height: 32, padding: 0, border: 'none' }}
            />
            <button
              type="button"
              className="btn btn-sm btn-primary"
              data-testid="product-color-save"
              onClick={() => void save(draft || effective.fill)}
            >
              Save color
            </button>
            {saved && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                data-testid="product-color-clear"
                onClick={() => void save(null)}
              >
                Use automatic
              </button>
            )}
          </>
        )}
      </div>
      {msg && (
        <div style={{ fontSize: 13, marginTop: 4 }} data-testid="product-color-msg">
          {msg}
        </div>
      )}
    </div>
  );
};

export default ProductColorField;
